import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format, addDays, isBefore, startOfToday } from "date-fns";
import { AlertCircle, Calendar as CalendarIcon, CheckCircle, Clock } from "lucide-react";

export default function AppointmentBooking() {
  const { user, isAuthenticated, loading } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedMemberId, setSelectedMemberId] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("10:00");
  const [endTime, setEndTime] = useState<string>("10:30");
  const [subject, setSubject] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [showDialog, setShowDialog] = useState(false);
  const [bookingStatus, setBookingStatus] = useState<"idle" | "success" | "error">("idle");
  const [bookingMessage, setBookingMessage] = useState<string>("");

  // Fetch EC members
  const { data: ecMembers = [], isLoading: membersLoading } = trpc.appointments.getECMembers.useQuery();

  // Book appointment mutation
  const bookAppointmentMutation = trpc.appointments.bookAppointment.useMutation({
    onSuccess: () => {
      setBookingStatus("success");
      setBookingMessage("Appointment request sent successfully! EC member will review and respond soon.");
      setTimeout(() => {
        setShowDialog(false);
        resetForm();
      }, 2000);
    },
    onError: (error) => {
      setBookingStatus("error");
      setBookingMessage(error.message || "Failed to book appointment");
    },
  });

  const resetForm = () => {
    setSelectedDate(undefined);
    setSelectedMemberId("");
    setStartTime("10:00");
    setEndTime("10:30");
    setSubject("");
    setDescription("");
    setBookingStatus("idle");
  };

  const handleBookAppointment = async () => {
    if (!selectedDate || !selectedMemberId || !subject) {
      setBookingStatus("error");
      setBookingMessage("Please fill in all required fields");
      return;
    }

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const ecMemberId = parseInt(selectedMemberId);

    bookAppointmentMutation.mutate({
      ecMemberId,
      appointmentDate: dateStr,
      startTime,
      endTime,
      subject,
      description: description || undefined,
    });
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date && !isBefore(date, startOfToday())) {
      setSelectedDate(date);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2">
            <CardHeader className="text-center">
              <CardTitle>Schedule an Appointment</CardTitle>
              <CardDescription>Please log in to book an appointment with EC members</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">You need to be logged in to schedule appointments.</p>
              <Button size="lg">Log In</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold mb-4">Schedule an Appointment</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Meet with Executive Committee members to discuss community matters
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Calendar and EC Members */}
          <div className="lg:col-span-2 space-y-6">
            {/* EC Members Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Select EC Member
                </CardTitle>
                <CardDescription>Choose the EC member you want to meet with</CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an EC member" />
                  </SelectTrigger>
                  <SelectContent>
                    {membersLoading ? (
                      <SelectItem value="loading" disabled>
                        Loading EC members...
                      </SelectItem>
                    ) : ecMembers.length === 0 ? (
                      <SelectItem value="none" disabled>
                        No EC members available
                      </SelectItem>
                    ) : (
                      ecMembers.map((member: any) => (
                        <SelectItem key={member.id} value={member.id.toString()}>
                          {member.position}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Calendar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Select Date
                </CardTitle>
                <CardDescription>Choose a date for your appointment (future dates only)</CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => isBefore(date, startOfToday())}
                  className="rounded-md border"
                />
              </CardContent>
            </Card>

            {/* Time Selection */}
            {selectedDate && (
              <Card>
                <CardHeader>
                  <CardTitle>Select Time</CardTitle>
                  <CardDescription>
                    Appointment on {format(selectedDate, "MMMM d, yyyy")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start-time">Start Time</Label>
                      <Input
                        id="start-time"
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="end-time">End Time</Label>
                      <Input
                        id="end-time"
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Booking Form */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Appointment Details</CardTitle>
                <CardDescription>Provide information about your appointment</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    placeholder="Meeting topic"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Additional details about your appointment"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                  />
                </div>

                {/* Summary */}
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <h4 className="font-semibold">Summary</h4>
                  {selectedMemberId && ecMembers.find((m: any) => m.id === parseInt(selectedMemberId)) && (
                    <p className="text-sm">
                      <strong>Member:</strong>{" "}
                      {ecMembers.find((m: any) => m.id === parseInt(selectedMemberId))?.position}
                    </p>
                  )}
                  {selectedDate && (
                    <p className="text-sm">
                      <strong>Date:</strong> {format(selectedDate, "MMM d, yyyy")}
                    </p>
                  )}
                  {selectedDate && (
                    <p className="text-sm">
                      <strong>Time:</strong> {startTime} - {endTime}
                    </p>
                  )}
                  {subject && (
                    <p className="text-sm">
                      <strong>Subject:</strong> {subject}
                    </p>
                  )}
                </div>

                <Button
                  onClick={() => setShowDialog(true)}
                  disabled={!selectedDate || !selectedMemberId || !subject}
                  className="w-full"
                  size="lg"
                >
                  Request Appointment
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Appointment Request</DialogTitle>
            <DialogDescription>
              Please review your appointment details before submitting
            </DialogDescription>
          </DialogHeader>

          {bookingStatus === "idle" && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <p className="text-sm">
                  <strong>Member:</strong>{" "}
                  {ecMembers.find((m: any) => m.id === parseInt(selectedMemberId))?.position}
                </p>
                <p className="text-sm">
                  <strong>Date:</strong> {selectedDate && format(selectedDate, "MMMM d, yyyy")}
                </p>
                <p className="text-sm">
                  <strong>Time:</strong> {startTime} - {endTime}
                </p>
                <p className="text-sm">
                  <strong>Subject:</strong> {subject}
                </p>
                {description && (
                  <p className="text-sm">
                    <strong>Description:</strong> {description}
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleBookAppointment}
                  disabled={bookAppointmentMutation.isPending}
                  className="flex-1"
                >
                  {bookAppointmentMutation.isPending ? "Submitting..." : "Submit Request"}
                </Button>
              </div>
            </div>
          )}

          {bookingStatus === "success" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                <p className="text-sm text-green-800 dark:text-green-200">{bookingMessage}</p>
              </div>
            </div>
          )}

          {bookingStatus === "error" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-800 dark:text-red-200">{bookingMessage}</p>
              </div>
              <Button onClick={() => setBookingStatus("idle")} className="w-full">
                Try Again
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
